import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuItemModel } from '../../models/menu-items/menu-item.model';
import { AddOrEditMenuItemComponent } from './add-or-edit-menu-item/add-or-edit-menu-item.component';
import { DeleteMenuItemComponent } from './delete-menu-item/delete-menu-item.component';
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { MenuService } from "../../services/menus/menu.service";
import { AuthenticationService } from "../../../share-modul/services/authentication/authentication.service";
import { MenuItemTypeEnum } from "../../enums/menu-item-type.enum";
import { debounceTime } from "rxjs/operators";

@Component({
  selector: 'app-menu-items',
  templateUrl: './menu-items.component.html',
  styleUrls: ['./menu-items.component.scss']
})
export class MenuItemsComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public listAllFoodMenuItems: Array<MenuItemModel> = new Array<MenuItemModel>();
  public listFilteredFoodMenuItems: Array<MenuItemModel> = new Array<MenuItemModel>();
  public listAllDrinksMenuItems: Array<MenuItemModel> = new Array<MenuItemModel>();
  public listFilteredDrinksMenuItems: Array<MenuItemModel> = new Array<MenuItemModel>();
  public imagePath: string = './assets/images/logo/category.png'
  public spinner: boolean=false;
  public items: { [name: string]: MenuItem[] } = {};
  public companyId: string;
  public MenuItemTypeEnum = MenuItemTypeEnum;
  public menuItemTypesOptions: Array<{id: number, name: string}>;
  public selectedMenuItemTypes: Array<MenuItemTypeEnum> = new Array<MenuItemTypeEnum>();
  public mobileMenuOptions: Array<any>;

  public searchTerm: string = '';
  private searchDebouncer = new Subject<void>();
  private RELOAD_DEBOUNCE_TIME = 1000;

  //#endregion
  //#region Constructor
  constructor(
    public dialogService: DialogService,
    public translate: TranslateService,
    private messageService: MessageService,
    private menuService: MenuService,
    private authService: AuthenticationService
  ) {
    this.translate.stream('HEADER.APP_LANGUAGE').subscribe( () => {
      this.addFunctionToAllMenuItems();
    });
  }

  ngOnInit(): void {
    this.companyId = this.authService.getCompanyId();
    this.loadMenuItemsForCompany();
    this.searchDebouncer.pipe(
      debounceTime(this.RELOAD_DEBOUNCE_TIME)
    ).subscribe(_ => this.filterMenuItems());
  }
  //#endregion

  //#region Public Methods
  createMenuItem(menuItemTypeId: number) {
    const ref = this.dialogService.open(AddOrEditMenuItemComponent, {
      data: {
        companyId: this.companyId,
        menuItemTypeId: menuItemTypeId
      },
      header: this.translate.instant(menuItemTypeId === MenuItemTypeEnum.Food ?
        'MENU_ITEM.ADD_FOOD_ITEM' : 'MENU_ITEM.ADD_DRINK_ITEM'),
      styleClass: 'dialog_menuItems'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadMenuItemsForCompany();
        this.messageService.add({severity:'success', summary: this.translate.instant("MENU_ITEM.MENU_ITEM_ADDED"), life: 3000});
      }
    });
  }

  editMenuItem(menuitem: MenuItemModel) {
    const ref = this.dialogService.open(AddOrEditMenuItemComponent, {
      data: {
        menuitem: menuitem,
        companyId: this.companyId
      },
      header: this.translate.instant(menuitem.menuItemTypeId === MenuItemTypeEnum.Food ?
        'MENU_ITEM.EDIT_FOOD_ITEM' : 'MENU_ITEM.EDIT_DRINK_ITEM'),
      styleClass: 'dialog_menuItems'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadMenuItemsForCompany();
        this.messageService.add({severity:'success', summary: this.translate.instant("MENU_ITEM.MENU_ITEM_EDITED"), life: 3000});
      }
    });
  }

  deleteMenuItem(id: string) {
    const ref = this.dialogService.open(DeleteMenuItemComponent, {
      data: {
        id: id
      },
      header: this.translate.instant('MENU_ITEM.DELETE_MENU_ITEM'),
      styleClass: 'dialog_long , ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadMenuItemsForCompany();
        this.messageService.add({severity:'success', summary: this.translate.instant("MENU_ITEM.MENU_ITEM_DELETED"), life: 3000});
      }
    });
  }

  loadMenuItemsForCompany() {
    this.listAllFoodMenuItems = this.listFilteredFoodMenuItems = new Array<MenuItemModel>();
    this.listAllDrinksMenuItems = this.listFilteredDrinksMenuItems = new Array<MenuItemModel>();
    this.spinner = true;
    this.menuService.listItemsForCompany(this.companyId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.listAllFoodMenuItems = this.listFilteredFoodMenuItems = response.filter((x: MenuItemModel) => x.menuItemTypeId === MenuItemTypeEnum.Food);
          this.listAllDrinksMenuItems = this.listFilteredDrinksMenuItems = response.filter((x: MenuItemModel) => x.menuItemTypeId === MenuItemTypeEnum.Drink);
          this.filterMenuItems();
          this.addFunctionToAllMenuItems();
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  addFunctionToAllMenuItems() {
    [...this.listAllDrinksMenuItems, ...this.listAllFoodMenuItems].forEach(menuItem => {
      this.items[menuItem.id] = [{
        label: this.translate.instant('EDIT'),
        command: () => {
          this.editMenuItem(menuItem);
        }
      },
        {
          label: this.translate.instant('DELETE'),
          command: () => {
            this.deleteMenuItem(menuItem.id);
          }
        }];
    });
    this.menuItemTypesOptions = [{id: MenuItemTypeEnum.Food, name: this.translate.instant('MENU_ITEM.FOOD')}, {id: MenuItemTypeEnum.Drink, name: this.translate.instant('MENU_ITEM.DRINKS')}];
    this.mobileMenuOptions = [{
      label: this.translate.instant('MENU_ITEM.ADD_FOOD'),
      command: () => {
        this.createMenuItem(MenuItemTypeEnum.Food);
      }
    },
      {
        label: this.translate.instant('MENU_ITEM.ADD_DRINKS'),
        command: () => {
          this.createMenuItem(MenuItemTypeEnum.Drink);
        }
      }];
  }

  determineImageUrl(pictureUrl: string) {
    const image = pictureUrl ? pictureUrl : this.imagePath;
    return encodeURI(image);
  }

  filterMenuItems(): void {
    this.listFilteredFoodMenuItems = this.listAllFoodMenuItems.filter(x => x.name.toLowerCase().includes(this.searchTerm.toLowerCase()));
    this.listFilteredDrinksMenuItems = this.listAllDrinksMenuItems.filter(x => x.name.toLowerCase().includes(this.searchTerm.toLowerCase()));
  }

  searchTermChange(): void {
    this.searchDebouncer.next();
  }
  //#endregion
}
